import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryIcon from '@mui/icons-material/History';
import { meetingsApi } from '../../../../services/api/meetingsApi';
import { formatDate } from '../../../../utils/dateUtils';
import LoadingSpinner from '../../../common/LoadingSpinner';
import EmptyState from '../../../common/EmptyState';

const HistoryTab = ({ meetingId }) => {
  const [statusHistory, setStatusHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadHistory = async () => {
      if (!meetingId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const history = await meetingsApi.getMeetingStatusHistory(meetingId);
        setStatusHistory(history || []);
      } catch (err) {
        console.error('Failed to load status history:', err);
        setError(err.message || 'Failed to load status history');
      } finally {
        setIsLoading(false);
      }
    };

    loadHistory();
  }, [meetingId]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!statusHistory || statusHistory.length === 0) {
    return (
      <EmptyState
        title="No Status History"
        description="Status changes will appear here once they are made."
      />
    );
  }

  // Get the previous status for each entry (to show transitions)
  const getFromStatus = (index) => {
    // The history is sorted newest first, so the "from" status is the next item in the array
    if (index < statusHistory.length - 1) {
      return statusHistory[index + 1].meetingStatus;
    }
    return null; // First status change has no "from" status
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Status History
      </Typography>

      <Box sx={{ position: 'relative' }}>
        {/* Timeline line */}
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            top: 24,
            bottom: 24,
            width: 2,
            backgroundColor: '#e2e8f0',
          }}
        />

        {/* History items */}
        {statusHistory.map((entry, index) => {
          const fromStatus = getFromStatus(index);
          const toStatus = entry.meetingStatus;

          return (
            <Box
              key={entry.id || index}
              sx={{
                display: 'flex',
                gap: 3,
                mb: 3,
                position: 'relative',
              }}
            >
              {/* Timeline dot */}
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  backgroundColor: toStatus?.color || '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  zIndex: 1,
                  border: '3px solid white',
                  boxShadow: '0 0 0 2px #e2e8f0',
                }}
              >
                <HistoryIcon sx={{ fontSize: 16, color: 'white' }} />
              </Box>

              {/* Content card */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  backgroundColor: '#f8fafc',
                  borderRadius: 1,
                  border: '1px solid #e2e8f0',
                }}
              >
                {/* Status transition */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  {fromStatus ? (
                    <>
                      <Chip
                        size="small"
                        label={fromStatus.name}
                        sx={{
                          backgroundColor: fromStatus.color || '#6b7280',
                          color: 'white',
                          fontWeight: 500,
                        }}
                      />
                      <ArrowForwardIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      Initial status:
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={toStatus?.name || 'Unknown'}
                    sx={{
                      backgroundColor: toStatus?.color || '#6b7280',
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                </Box>

                {/* Reason/Notes */}
                {entry.reason && (
                  <Typography variant="body2" sx={{ mb: 1.5, color: '#374151' }}>
                    {entry.reason}
                  </Typography>
                )}

                {/* Metadata */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(entry.createdAt)}
                  </Typography>
                  {entry.createdBy && (
                    <Typography variant="caption" color="text.secondary">
                      by {entry.createdBy}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default HistoryTab;
